import React, { useState, useRef, useEffect, useMemo } from 'react';

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

interface SelectContextType {
  value?: string;
  /** Each SelectItem's value mapped to what it renders, read from the JSX. */
  labels: Map<string, React.ReactNode>;
  onValueChange?: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SelectContext = React.createContext<SelectContextType>({
  labels: new Map(),
  open: false,
  setOpen: () => {},
});

/**
 * The trigger used to print the raw value, so a picked customer read as its
 * UUID and a priority as `critical`. The label has to come from the JSX rather
 * than from mounted items: SelectContent renders nothing while it is shut,
 * which is exactly when the trigger is on screen. So walk the children for
 * SelectItem elements. Items rendered from inside another component are
 * unseen by this walk; every call site writes them in its own JSX.
 */
function collectLabels(node: React.ReactNode, labels: Map<string, React.ReactNode>) {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as { value?: unknown; children?: React.ReactNode };
    if (child.type === SelectItem && typeof props.value === 'string') {
      labels.set(props.value, props.children);
      return;
    }
    collectLabels(props.children, labels);
  });
}

export function Select({ value, onValueChange, children }: SelectProps) {
  const [open, setOpen] = useState(false);
  const labels = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    collectLabels(children, map);
    return map;
  }, [children]);
  return (
    <SelectContext.Provider value={{ value, onValueChange, labels, open, setOpen }}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { open, setOpen } = React.useContext(SelectContext);
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className={`w-full flex items-center justify-between bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50 transition-colors ${className}`}
    >
      {children}
      <svg className="w-4 h-4 text-fieldiq-muted ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

/**
 * No value shows the placeholder. A value with no matching item (its options
 * still loading, or gone) shows an em dash: never the raw value, and never the
 * placeholder, which would say nothing is picked when something is.
 */
export function SelectValue({ placeholder }: { placeholder?: string }) {
  const { value, labels } = React.useContext(SelectContext);
  if (!value) return <span className="text-fieldiq-muted">{placeholder || 'Select...'}</span>;
  return <span className="text-white">{labels.has(value) ? labels.get(value) : '—'}</span>;
}

export function SelectContent({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { open, setOpen } = React.useContext(SelectContext);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, setOpen]);

  if (!open) return null;
  return (
    <div ref={ref} className={`absolute z-50 mt-1 w-full bg-fieldiq-card border border-fieldiq-border rounded-lg shadow-xl max-h-60 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
}

export function SelectItem({ value, children }: { value: string; children: React.ReactNode }) {
  const ctx = React.useContext(SelectContext);
  const selected = ctx.value === value;
  return (
    <button
      type="button"
      onClick={() => {
        ctx.onValueChange?.(value);
        ctx.setOpen(false);
      }}
      className={`w-full text-left px-3 py-2 text-sm hover:bg-fieldiq-border/50 transition-colors ${
        selected ? 'text-fieldiq-accent bg-fieldiq-accent/10' : 'text-white'
      }`}
    >
      {children}
    </button>
  );
}
